/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import SigPad from "signature_pad";
import _debounce from "lodash/debounce";

type SignaturePadProps = {
  width?: number;
  height?: number;
  options?: Record<string, unknown>;
  setMinimumSignatureSizeReached: (minimumSignatureSizeReached: boolean) => void;
  debounceInterval?: number;
  disabled?: boolean;
};

export type SignaturePadRef = { clear: () => void; signaturePadInstance?: SigPad };

export const SignaturePad = forwardRef<SignaturePadRef, SignaturePadProps>(function SignaturePad(
  { width, height, options, setMinimumSignatureSizeReached, debounceInterval = 150, disabled = false },
  ref
) {
  const canvasRef = useRef<HTMLCanvasElement>(null!);
  const signaturePadInstanceRef = useRef<SigPad>(null!);

  useImperativeHandle(ref, () => {
    return {
      clear,
      signaturePadInstance: signaturePadInstanceRef.current,
    };
  });

  const [canvasSize, setCanvasSize] = useState<{
    width?: number;
    height?: number;
  }>({
    width: canvasRef?.current?.width,
    height: canvasRef?.current?.height,
  });

  useEffect(() => {
    if (canvasRef.current) {
      if (!width || !height) {
        canvasRef.current.style.width = "100%";
      }
      scaleCanvas();
      let signaturePadInstance = new SigPad(canvasRef.current, options);
      if (!width || !height) {
        window.addEventListener("resize", _debounce(scaleCanvas, debounceInterval));
      }
      signaturePadInstance.addEventListener("endStroke", () => {
        setMinimumSignatureSizeReached(signaturePadInstance.toDataURL("image/svg+xml").length > 2000);
      });

      signaturePadInstanceRef.current = signaturePadInstance;
    }
  }, []);

  function scaleCanvas() {
    if (!canvasRef.current) {
      return;
    }

    const context = canvasRef.current.getContext("2d");
    const imageData = context?.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const canvasWidth = (width || canvasRef.current.offsetWidth) * ratio;
    const canvasHeight = (height || canvasRef.current.offsetHeight) * ratio;

    if (canvasWidth === canvasSize.width && canvasHeight === canvasSize.height) {
      return;
    }

    canvasRef.current.width = canvasWidth;
    canvasRef.current.height = canvasHeight;
    setCanvasSize({
      width: canvasRef.current.width,
      height: canvasRef.current.height,
    });

    canvasRef.current.getContext("2d")?.scale(ratio, ratio);

    if (!imageData) {
      return;
    }

    signaturePadInstanceRef.current?.redraw();
  }

  function clear() {
    signaturePadInstanceRef?.current?.clear();
    setMinimumSignatureSizeReached(false);
  }

  return (
    <canvas
      ref={canvasRef}
      style={{
        pointerEvents: disabled ? "none" : "auto",
        opacity: disabled ? 0.5 : 1,
      }}
    />
  );
});
